import { SuggestionItem } from '../types';

export const DOMAIN_OVERVIEW_SUGGESTIONS: SuggestionItem[] = [
  {
    id: 'overview.esVersion',
    label: 'Engine Version',
    description: 'Elasticsearch/OpenSearch engine version',
    keywords: ['es version', 'elasticsearch', 'opensearch', 'engine', 'version'],
    category: 'domain-overview',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.esVersion',
    formatter: (v) => `**Engine Version:** ${v || 'N/A'}`,
  },
  {
    id: 'overview.cellId',
    label: 'Cell ID',
    description: 'Cell identifier for the domain',
    keywords: ['cell', 'cell id', 'cellid'],
    category: 'domain-overview',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.cellId',
    formatter: (v) => `**Cell ID:** ${v || 'N/A'}`,
  },
  {
    id: 'overview.haproxyVersion',
    label: 'HAProxy Version',
    description: 'HAProxy version running on the domain',
    keywords: ['haproxy', 'ha proxy', 'proxy version'],
    category: 'domain-overview',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.HAProxyVersion',
    formatter: (v) => `**HAProxy Version:** ${v || 'N/A'}`,
  },
  {
    id: 'overview.haproxyType',
    label: 'HAProxy Type',
    description: 'HAProxy type (e.g. NLB, CLB)',
    keywords: ['haproxy type', 'ha proxy type', 'ha proxy', 'proxy type', 'nlb', 'clb'],
    category: 'domain-overview',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.HAProxyType',
    formatter: (v) => `**HAProxy Type:** ${v || 'N/A'}`,
  },
  {
    id: 'overview.pinnedVFIDomain',
    label: 'Pinned VFI (Domain)',
    description: 'Domain-level pinned VFI',
    keywords: ['pinned vfi', 'vfi domain', 'pinned', 'vfi'],
    category: 'domain-overview',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.pinnedVFIDomain',
    formatter: (v) => `**Pinned VFI (Domain):** ${v || 'No Pinned VFI'}`,
  },
  {
    id: 'overview.pinnedVFIClient',
    label: 'Pinned VFI (Client)',
    description: 'Client-level pinned VFI',
    keywords: ['pinned vfi client', 'vfi client', 'client vfi'],
    category: 'domain-overview',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.pinnedVFIClient',
    formatter: (v) => `**Pinned VFI (Client):** ${v || 'No Pinned VFI'}`,
  },
  {
    id: 'overview.isSensitiveDomain',
    label: 'Sensitive Domain',
    description: 'Whether this is a sensitive domain',
    keywords: ['sensitive', 'sensitive domain', 'sensitive customer'],
    category: 'domain-overview',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.IsSensitiveDomain',
    formatter: (v) => `**Sensitive Domain:** ${v || 'N/A'}`,
  },
  {
    id: 'overview.isSensitiveClient',
    label: 'Sensitive Client',
    description: 'Whether this is a sensitive client',
    keywords: ['sensitive client', 'sensitive customer client'],
    category: 'domain-overview',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.IsSensitiveClient',
    formatter: (v) => `**Sensitive Client:** ${v || 'N/A'}`,
  },
  {
    id: 'overview.isDomainIsolated',
    label: 'Domain Isolated',
    description: 'Whether the domain is isolated',
    keywords: ['isolated', 'domain isolated', 'isolation'],
    category: 'domain-overview',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.IsDomainIsolated',
    formatter: (v) => `**Domain Isolated:** ${v || 'N/A'}`,
  },
  {
    id: 'overview.carbonDomain',
    label: 'Carbon Domain',
    description: 'Whether this is a carbon (multi-AZ) domain',
    keywords: ['carbon', 'carbon domain', 'multi-az', 'multi az'],
    category: 'domain-overview',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.carbonDomain',
    formatter: (v) => `**Carbon Domain:** ${v || 'N/A'}`,
  },
  {
    id: 'overview.createDateTime',
    label: 'Domain Create Date/Time',
    description: 'When the domain was created',
    keywords: ['create date', 'created', 'creation time', 'domain age', 'create datetime'],
    category: 'domain-overview',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.DomainCreateDateTime',
    formatter: (v) => {
      if (!v) return '**Domain Create Date/Time:** N/A';
      const date = v.split('T')[0] || 'N/A';
      const time = v.split('T')[1]?.split('.')[0] || 'N/A';
      return `**Domain Create Date/Time:** ${date} / ${time} UTC`;
    },
  },
  {
    id: 'overview.packageType',
    label: 'Packages Associated',
    description: 'Package type associated on the domain',
    keywords: ['package', 'packages', 'package type', 'associated packages'],
    category: 'domain-overview',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.PackageTypeAssociatedOnDomain',
    formatter: (v) => `**Packages Associated:** ${v || 'N/A'}`,
  },
  {
    id: 'overview.dragonstoneDomain',
    label: 'Remote Store Enabled',
    description: 'Whether remote store is enabled (Dragonstone)',
    keywords: ['remote store', 'dragonstone', 'remote storage'],
    category: 'domain-overview',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.dragonstoneDomain',
    formatter: (v) => `**Remote Store Enabled:** ${v || 'N/A'}`,
  },
  {
    id: 'overview.remotePublication',
    label: 'Remote Publication Enabled',
    description: 'Whether remote publication is enabled',
    keywords: ['remote publication', 'publication', 'remote pub'],
    category: 'domain-overview',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.IsRemotePublicationEnabled',
    formatter: (v) => `**Remote Publication Enabled:** ${v || 'N/A'}`,
  },
  {
    id: 'overview.ec2Instances',
    label: 'EC2 Instance Count',
    description: 'Number of EC2 instances in the domain',
    keywords: ['ec2', 'instances', 'instance count', 'nodes count'],
    category: 'domain-overview',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.resources.ALL.ec2Instances',
    formatter: (v) => `**EC2 Instance Count:** ${Array.isArray(v) ? v.length : 'N/A'}`,
  },
  {
    id: 'overview.elbInfo',
    label: 'ELB Info',
    description: 'Load balancer name and target group',
    keywords: ['elb', 'load balancer', 'target group', 'nlb'],
    category: 'domain-overview',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain.resources.ALL.elb',
    formatter: (v) => {
      if (!v) return '**ELB Info:** N/A';
      return `**ELB Info:**\n- Load Balancer: ${v.loadBalancerName || 'N/A'}\n- Target Group: ${v.targetGroupName || 'N/A'}`;
    },
  },
  {
    id: 'overview.allFields',
    label: 'Domain Overview (All)',
    description: 'Full domain overview with all fields',
    keywords: ['overview', 'domain overview', 'all overview', 'domain details', 'domain info'],
    category: 'domain-overview',
    queryId: 'RESOURCE_INFO',
    fieldPath: 'domain',
    formatter: (d) => {
      if (!d) return '⚠️ No overview data available.';
      return [
        '## 📋 Domain Overview',
        '| Field | Value |',
        '|-------|-------|',
        `| Engine Version | ${d.esVersion || 'N/A'} |`,
        `| Cell ID | ${d.cellId || 'N/A'} |`,
        `| HAProxy Version | ${d.HAProxyVersion || 'N/A'} |`,
        `| HAProxy Type | ${d.HAProxyType || 'N/A'} |`,
        `| Pinned VFI (Domain) | ${d.pinnedVFIDomain || 'N/A'} |`,
        `| Pinned VFI (Client) | ${d.pinnedVFIClient || 'N/A'} |`,
        `| Sensitive Domain | ${d.IsSensitiveDomain || 'N/A'} |`,
        `| Sensitive Client | ${d.IsSensitiveClient || 'N/A'} |`,
        `| Domain Isolated | ${d.IsDomainIsolated || 'N/A'} |`,
        `| Carbon Domain | ${d.carbonDomain || 'N/A'} |`,
        `| Remote Store | ${d.dragonstoneDomain || 'N/A'} |`,
        `| Remote Publication | ${d.IsRemotePublicationEnabled || 'N/A'} |`,
        `| Packages | ${d.PackageTypeAssociatedOnDomain || 'N/A'} |`,
        `| Created | ${d.DomainCreateDateTime || 'N/A'} |`,
      ].join('\n');
    },
  },
];
